import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { sendWorkshopRegistrationEmail } from '@chancenwerk/workshops/api/sendWorkshopRegistrationMail.js';
import { WorkshopAppointment } from '@chancenwerk/workshops/entities/WorkshopAppointment.js';
import { WorkshopAttendance } from '@chancenwerk/workshops/entities/WorkshopAttendance.js';
import { WorkshopAttendanceStatus } from '@chancenwerk/workshops/entities/WorkshopAttendanceStatus.js';
import { WorkshopTrainingAttendance } from '@chancenwerk/workshops/entities/WorkshopTrainingAttendance.js';
import { DateTime } from 'luxon';

export const createWorkshopRegistration = registerMutation({
  type: () => Boolean,
  operationName: 'createWorkshopRegistration',
  resolve: (appointmentID: string) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const appointment = await WorkshopAppointment.getByID<WorkshopAppointment>(appointmentID)({
        ...WorkshopAppointment.allFields,
        workshop: {
          title: 1,
          appointmentInfo: 1,
          requiresTraining: 1,
        },
      });

      if (!appointment) {
        throw new Error('Appointment not found');
      }

      const attendance = await loadMongoData(WorkshopTrainingAttendance, {
        userID: user._id,
        workshopID: appointment.workshopID,
        $and: [
          {
            date: {
              $gte: (appointment.start ?? DateTime.now()).startOf('day').toJSDate(),
            },
          },
          {
            date: {
              $lte: (appointment.start ?? DateTime.now()).endOf('day').toJSDate(),
            },
          },
        ],
      });

      if (attendance.length > 0) {
        throw new Error('Already registered');
      }

      if (appointment.workshop?.requiresTraining) {
        const newAttendance = new WorkshopTrainingAttendance({
          userID: user._id,
          workshopID: appointment.workshopID,
          date: appointment.start,
          state: WorkshopAttendanceStatus.REGISTERED,
        });
        await newAttendance.create();
      } else {
        const newAttendance = new WorkshopAttendance({
          userID: user._id,
          workshopID: appointment.workshopID,
          date: appointment.start,
          state: WorkshopAttendanceStatus.REGISTERED,
        });
        await newAttendance.create();
      }

      await sendWorkshopRegistrationEmail(appointmentID, user._id)();

      return true;
    };
  },
  options: {},
  params: [{ type: () => String, name: 'appointmentID' }],
});
